import { useState } from "react";
import { useDispatch } from "react-redux";
import wrapperHOC from "@Utils/wrapperHOC";
import { createLead } from "services/leadsHelper";
import Input from "@Components/Input";
import { RegionDropdown } from "@Components/PartnerLeadFormModal/components/dropdown";
import ToastNotificationDispatcher from "@Components/ToastNotification/actions";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { countryCodes } from "@Components/LeadFormModal/data/countryCodes";
/**
 * ⚠️  `/mobile`, NOT the default metadata and NOT `/max`.
 *
 * The default metadata carries no per-type detail, so it accepts an 8-digit Chinese
 * number — the exact case QA filed. `/max` rejects it but ships 39KB gzipped of metadata;
 * `/mobile` rejects it too at 23KB, and agreed with `/max` on every case tried (IN 8/10/12,
 * CN 8/11, GB 8/10, AU 9, US 10). Only /group-booking pays for it — this form is imported
 * by that page alone.
 */
import { getCountries, getCountryCallingCode, isValidPhoneNumber } from "libphonenumber-js/mobile";
import { getDurationOptions, getRoomOptions } from "./constants";
import useTranslationHook from "hooks/useTranslationHook";
import styles from "./GroupBookingForm.module.scss";

/**
 * The Group Booking enquiry form — a two-step port of the one on the page this
 * replaces (s.web `views/_components/GroupBookingsV2/components/GroupBookingForm`).
 *
 * ⚠️  SELF-CONTAINED ON PURPOSE. The obvious shortcut was to open the site's shared
 * `LeadFormModal`, and that was tried: it hits the same `/api/v0/leads/` endpoint and
 * its reducer even uses the same field names. It does not work here. That modal is
 * always opened from a property or a search region, so a location rides along inside
 * `inventory`; this is a standalone marketing page with neither, and the API answers
 * `400 {"message":"Error location-not-present"}`. Bending the shared modal to fit
 * meant changing routing every other page depends on, which broke more than it fixed.
 *
 * So this owns its own state and posts its own lead. Nothing outside this folder is
 * modified — `createLead`, `updateLead` and `RegionDropdown` are imported and used as
 * they are.
 *
 * ⚠️  ONE `createLead` ON FINAL SUBMIT — NOT create-then-update.
 *
 * The old page created the lead on step one and merged step two into it with
 * `updateLead`. That cannot work here for a logged-out visitor, and the API is explicit
 * about it:
 *
 *   PUT /api/v0/leads/{uuid}       -> 404  (no anonymous update route at all)
 *   PUT /api/v0/auth/leads/{uuid}  -> 401  (the route exists, it just needs a session)
 *
 * So step two's PUT returned a Rails 404 page every time and the accommodation answers
 * were silently dropped — the user saw "Something went wrong" after filling everything
 * in. Both steps are now held in state and posted together, which always succeeds and
 * always carries the full picture.
 *
 * ⚠️  THE COST, worth knowing: a group that abandons after step one is no longer
 * captured, which the old page did manage. Getting that back means updating through
 * `/api/v0/auth/leads/` — which needs the visitor signed in, and they are not.
 */
interface GroupBookingFormProps {
  /** Seeded from the hero's slider — the whole reason this page has one. */
  groupSize: number;
  onClose: () => void;
}

/**
 * ⚠️  THE SHARED LIST, NOT A HAND-PICKED ONE.
 *
 * This was six codes I typed out — +91, +44, +1, +61, +353, +34 — which quietly told
 * anyone outside those six countries that they could not enquire. `countryCodes` is the
 * site's own list: 251 entries, each with its flag, and it is what every other form on the
 * site offers. It is used for the DROPDOWN only — its `maxLength` column is a ceiling, not
 * a length (Australia is listed at 15, Indonesia at 17), so validation does not read it.
 */
/**
 * Every calling code libphonenumber actually has data for.
 *
 * ⚠️  NEEDED BECAUSE FIVE DROPDOWN LABELS ARE MALFORMED, and strict validation would block
 * those countries outright — worse than the loose rule it replaces. The NANP territories
 * are listed with the leading 1 missing (`+767` Dominica, `+876` Jamaica, `+868` Trinidad
 * — really +1767 / +1876 / +1868), `+5997` carries a prefix digit past Caribbean
 * Netherlands' +599, and `+4779` does the same to Norway's +47. Tested: a real example
 * mobile validates for 241 of the 246 entries, and for those five nothing can.
 *
 * So a code we have data for is validated properly, and one we do not falls back to a
 * length sanity check rather than rejecting a whole market. Fix the five labels in
 * `countryCodes` and they graduate to strict validation automatically.
 */
const KNOWN_CALLING_CODES = new Set(getCountries().map((c) => `+${getCountryCallingCode(c)}`));

/** Loose bounds for the codes above that libphonenumber cannot place. */
// PLG-3349: the closed <select> is 132px, and the full description overflowed it —
// "🇺🇸 United States of America (+1)" rendered as a clipped "United State". The flag
// already says which country this is, and the code is the part that pairs with the number
// field beside it, so the name is the only part that can go.
//
// Every entry's description is "<flag> <name>", verified across all 251, so the flag is
// the first space-separated token. Longest result is "🇨🇨 +61 891" at 10 chars.
const flagOf = (description: string) => description.split(" ")[0];

const FALLBACK_MIN_DIGITS = 6;
const FALLBACK_MAX_DIGITS = 15;

const MIN_STUDENTS = 1;
const MAX_STUDENTS = 50;

const GroupBookingForm = ({ groupSize, onClose }: GroupBookingFormProps) => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();
  const toast = new ToastNotificationDispatcher(dispatch);

  const [step, setStep] = useState<1 | 2>(1);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [region, setRegion] = useState<any>({ name: "", location: {} });

  const [one, setOne] = useState({
    name: "",
    email: "",
    phoneCode: "+91",
    phone: "",
    students: String(groupSize),
  });
  const [two, setTwo] = useState({
    configuration: "",
    moveIn: "",
    duration: "",
    requirement: "",
  });

  const setField = (setter: any, key: string) => (value: string) => {
    setter((prev: any) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  /**
   * How many digits the FIELD accepts, for the country currently selected.
   *
   * ⚠️  THIS IS THE INPUT CAP, NOT THE VALIDATION. `countryCodes.maxLength` is a generous
   * ceiling rather than a real length — Australia is listed at 15, Indonesia at 17 — which
   * makes it useless for deciding whether a number is valid (libphonenumber does that on
   * submit) but exactly right for stopping someone typing 21 digits into the box. Erring
   * generous here is deliberate: the cap must never block a digit a real number needs.
   */
  const phoneMaxDigits =
    countryCodes.find((c) => c.label === one.phoneCode)?.maxLength ?? FALLBACK_MAX_DIGITS;

  const validateOne = () => {
    const next: Record<string, string> = {};
    if (!one.name.trim()) next.name = t("groupbooking.v2.form.error.required");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(one.email.trim()))
      next.email = t("groupbooking.v2.form.error.email");
    // ⚠️  REAL PER-COUNTRY VALIDATION, NOT A LENGTH RANGE.
    //
    // This was `^\d{6,maxLength}$`, taking the ceiling from `countryCodes`. Two problems:
    // the floor of 6 was invented — that list carries no minimum at all, 251 entries and
    // zero `minLength` — so India accepted 6 digits and China 6; and a min..max range
    // cannot express a real numbering plan anyway. GB mobiles are 7, 9 or 10 digits with
    // no 8, NG skips 9; a range waves those through.
    //
    // So the number is validated as an international one, `+<code><digits>`. That also
    // settles the shared calling codes: +1 resolves US vs CA by prefix instead of us
    // guessing which country the dropdown meant.
    const digits = one.phone.trim();
    // Strict where libphonenumber has data for the code; a length check where it does not.
    // ⚠️  EXACT MATCH, NOT `startsWith`. THIS IS WHY THE SHARED DATA IS LEFT ALONE.
    //
    // `startsWith` claimed a code was known whenever a real calling code was a PREFIX of
    // it, and five entries in the shared list carry a merged country+area code:
    // Jamaica "+876", Trinidad "+868", Dominica "+767", Bonaire "+5997", Svalbard "+4779".
    // "+868".startsWith("+86") matches CHINA, so a valid Trinidad number was parsed as
    // Chinese and rejected — and the same for the other four.
    //
    // `countryCodes` is imported by 11 files, so correcting the labels there was the wrong
    // place to fix it. An exact lookup means those five simply do not resolve to a country,
    // fall through to the digit-range check below, and are accepted. They get a length
    // check rather than full libphonenumber validation, which is the right trade: a looser
    // check on five entries beats rejecting every number from them.
    const canValidate = KNOWN_CALLING_CODES.has(one.phoneCode);
    const phoneOk =
      /^\d+$/.test(digits) &&
      (canValidate
        ? isValidPhoneNumber(`${one.phoneCode}${digits}`)
        : digits.length >= FALLBACK_MIN_DIGITS && digits.length <= FALLBACK_MAX_DIGITS);
    if (!phoneOk) next.phone = t("groupbooking.v2.form.error.phone");
    if (!region?.name?.trim()) next.region = t("groupbooking.v2.form.error.required");
    const n = Number(one.students);
    if (!n || n < MIN_STUDENTS || n > MAX_STUDENTS)
      next.students = `Enter a number between ${MIN_STUDENTS} and ${MAX_STUDENTS}`;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const validateTwo = () => {
    const next: Record<string, string> = {};
    if (!two.configuration) next.configuration = t("groupbooking.v2.form.error.required");
    if (!two.moveIn) next.moveIn = t("groupbooking.v2.form.error.required");
    if (!two.duration) next.duration = t("groupbooking.v2.form.error.required");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submitOne = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateOne()) return;
    gtagEventEmitter({
      action: "group_booking_form_step_one",
      category: "group_booking",
      label: `group_of_${one.students}`,
    });
    setStep(2);
  };

  const submitTwo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !validateTwo()) return;
    setBusy(true);

    // ⚠️  THE REGION'S FIELDS ARE NESTED UNDER `.location`. THEY ARE NOT ON THE TOP LEVEL.
    //
    // `RegionDropdown` hands back
    //   { id, name, location: { location_place_id, location_coordinates: { lat, lng }, name } }
    //
    // Reading `region.location_place_id` and `region.location_coordinates` off the top
    // level returned undefined for both, and the `if (...)` guards below then dropped them
    // silently, so every lead went out with no place id and no coordinates. The API refuses
    // a lead without a location, which is the "Something went wrong" toast on submit.
    //
    // `PartnerLeadFormModal/utils.ts:150` is the reference, and it is the same dropdown.
    const place = region?.location || {};
    const coords = place.location_coordinates || {};

    const lead: Record<string, unknown> = {
      state: "created",
      user_details: {
        name: one.name.trim(),
        phone: `${one.phoneCode}${one.phone.trim()}`,
        email: one.email.trim(),
      },
      data: {
        no_of_students: Number(one.students),
        configuration: two.configuration,
        // DD-MM-YYYY, which is the format the page this replaces sent. The native date
        // control gives YYYY-MM-DD, so it is reshaped rather than passed through.
        move_in_date: two.moveIn.split("-").reverse().join("-"),
        lease_duration: two.duration,
        any_specific_requirement: two.requirement,
      },
      source_details: {
        final_url: typeof window !== "undefined" ? window.location.href : "",
        referring_domain: typeof document !== "undefined" ? document.referrer : "",
      },
    };
    if (region?.id) lead.region_id = region.id;
    if (coords.lat) lead.location_coordinates_latitude = coords.lat;
    if (coords.lng) lead.location_coordinates_longitude = coords.lng;
    if (place.location_place_id) lead.location_place_id = place.location_place_id;
    // The dropdown's own label is the fallback: the nested name is the canonical one, but
    // `region.name` is what the field displayed, so it is never empty when one is chosen.
    if (place.name || region?.name) lead.location_place_name = place.name || region.name;

    const response: any = await createLead({ lead });
    setBusy(false);

    // The lead is under `response.data`; there is no top-level uuid.
    if (response?.error || !response?.data?.uuid) {
      toast.openToast({
        title: t("groupbooking.v2.form.toast.errortitle"),
        message: t("groupbooking.v2.form.toast.errormessage"),
        type: "error",
      });
      return;
    }

    gtagEventEmitter({ action: "group_booking_form_submit", category: "group_booking" });
    toast.openToast({
      title: t("groupbooking.v2.form.toast.successtitle"),
      message: t("groupbooking.v2.form.toast.successmessage"),
    });
    onClose();
  };

  /**
   * ⚠️  THE SHARED `Input` CANNOT SHOW AN ERROR MESSAGE — IT HAS TO BE PRINTED HERE.
   *
   * Its error block is commented out in the component ("commented out for future use"),
   * so it renders an empty fragment however good the `error` prop is; `showErrorMessage`
   * only reddens the border — and it keys that off the PROP ALONE, not the prop plus an
   * error, so passing it unconditionally turned every field red before anyone had typed
   * anything. It is bound to each field's own error below.
   *
   * That made every failure on this step SILENT: an 11-digit
   * Indian number is invalid, validation refused to advance, and nothing on screen said
   * so — "Next" simply did nothing. The message is rendered here, under the field.
   */
  const withError = (key: string, node: React.ReactNode) => (
    <div className={styles.fieldWrap}>
      {node}
      {errors[key] ? (
        <span className={styles.error} role="alert">
          {errors[key]}
        </span>
      ) : null}
    </div>
  );

  /**
   * ⚠️  `@Components/Input`, NOT a bare <input>.
   *
   * The first cut styled its own fields and they read as a different product: the site's
   * inputs put the label INSIDE the control as a floating legend on the border, and
   * carry their own focus, error and filled states. Hand-rolling that here would have
   * meant maintaining a second, drifting copy of it. The component is imported and used
   * as it is — nothing in it is modified.
   */

  if (step === 2) {
    return (
      <form className={styles.form} onSubmit={submitTwo} noValidate>
        <h2 className={styles.title}>{t("groupbooking.v2.form.step2.title")}</h2>
        <p className={styles.subtitle}>{t("groupbooking.v2.form.step2.subtitle")}</p>

        <label className={styles.selectField} htmlFor="gb-configuration">
          <span className={styles.selectLabel}>
            {t("groupbooking.v2.form.label.roomtype")} <span className={styles.required}>*</span>
          </span>
          <select
            id="gb-configuration"
            value={two.configuration}
            onChange={(e) => setField(setTwo, "configuration")(e.target.value)}
          >
            <option value="">{t("groupbooking.v2.form.placeholder.roomtype")}</option>
            {getRoomOptions().map((room) => (
              <option key={room.value} value={room.value}>
                {t(room.label)}
              </option>
            ))}
          </select>
          {errors.configuration ? (
            <span className={styles.error} role="alert">
              {errors.configuration}
            </span>
          ) : null}
        </label>

        <div className={styles.row}>
          {/* ⚠️  NOT `Input type="date"`.
              Input renders its label inside the box, in the same place a native date
              control already prints its own `dd/mm/yyyy` — the two sat on top of each
              other. A date field is never visually empty, so the label has to live on
              the border, which is exactly the treatment the selects beside it use. */}
          <label className={styles.selectField} htmlFor="gb-moveIn">
            <span className={styles.selectLabel}>
              {t("groupbooking.v2.form.label.movein")} <span className={styles.required}>*</span>
            </span>
            <input
              id="gb-moveIn"
              type="date"
              className={styles.dateField}
              value={two.moveIn}
              onChange={(e) => setField(setTwo, "moveIn")(e.target.value)}
            />
            {/* ⚠️  AN OVERLAY, BECAUSE A DATE INPUT CANNOT TAKE A `placeholder`.
                The attribute is ignored on type="date", and ::before/::after do not render
                on an input either. Chrome fills an empty one with its own "dd/mm/yyyy", but
                iOS Safari draws nothing at all, so the box read as blank next to two selects
                that both say what they want. This span sits over the field and is
                pointer-events: none, so taps still reach the input and open the picker. */}
            {!two.moveIn ? (
              <span className={styles.datePlaceholder} aria-hidden="true">
                {t("groupbooking.v2.form.placeholder.movein")}
              </span>
            ) : null}
            {errors.moveIn ? (
              <span className={styles.error} role="alert">
                {errors.moveIn}
              </span>
            ) : null}
          </label>
          <label className={styles.selectField} htmlFor="gb-duration">
            <span className={styles.selectLabel}>
              {t("groupbooking.v2.form.label.duration")} <span className={styles.required}>*</span>
            </span>
            <select
              id="gb-duration"
              value={two.duration}
              onChange={(e) => setField(setTwo, "duration")(e.target.value)}
            >
              <option value="">{t("groupbooking.v2.form.placeholder.duration")}</option>
              {getDurationOptions().map((d) => (
                <option key={d.value} value={d.value}>
                  {t(d.label)}
                </option>
              ))}
            </select>
            {errors.duration ? (
              <span className={styles.error} role="alert">
                {errors.duration}
              </span>
            ) : null}
          </label>
        </div>

        <Input
          type="textArea"
          label={t("groupbooking.v2.form.label.requirement")}
          id="gb-requirement"
          value={two.requirement}
          onChange={setField(setTwo, "requirement")}
        />

        {/* Split into five runs rather than one interpolated string: the two links are
            real anchors, so the sentence has to be assembled around them — and word
            order differs enough between languages that each run needs its own key. */}
        <p className={styles.terms}>
          {t("groupbooking.v2.form.terms.before")}{" "}
          <a href="/terms">{t("groupbooking.v2.form.terms.terms")}</a>{" "}
          {t("groupbooking.v2.form.terms.and")}{" "}
          <a href="/privacy">{t("groupbooking.v2.form.terms.privacy")}</a>{" "}
          {t("groupbooking.v2.form.terms.after")}
        </p>
        <button type="submit" className={styles.submit} disabled={busy}>
          {busy
            ? t("groupbooking.v2.form.button.submitting")
            : t("groupbooking.v2.form.button.submit")}
        </button>
      </form>
    );
  }

  return (
    <form className={styles.form} onSubmit={submitOne} noValidate>
      <h2 className={styles.title}>{t("groupbooking.v2.form.step1.title")}</h2>
      <p className={styles.subtitle}>{t("groupbooking.v2.form.step1.subtitle")}</p>

      {withError(
        "name",
        <Input
          type="text"
          label={t("groupbooking.v2.form.label.fullname")}
          id="gb-name"
          showErrorMessage={!!errors.name}
          required
          value={one.name}
          error={errors.name}
          onChange={setField(setOne, "name")}
        />,
      )}
      {withError(
        "email",
        <Input
          type="email"
          label={t("groupbooking.v2.form.label.email")}
          id="gb-email"
          showErrorMessage={!!errors.email}
          required
          value={one.email}
          error={errors.email}
          onChange={setField(setOne, "email")}
        />,
      )}

      <div className={styles.row}>
        <label className={`${styles.selectField} ${styles.code}`} htmlFor="gb-code">
          <span className={styles.selectLabel}>
            {t("groupbooking.v2.form.label.code")} <span className={styles.required}>*</span>
          </span>
          <select
            id="gb-code"
            value={one.phoneCode}
            onChange={(e) => {
              const code = e.target.value;
              const max =
                countryCodes.find((c) => c.label === code)?.maxLength ?? FALLBACK_MAX_DIGITS;
              // Switching from a long plan to a short one (AU 15 -> IN 10) would otherwise
              // leave digits in the box that the new country cannot have.
              setOne((prev: typeof one) => ({
                ...prev,
                phoneCode: code,
                phone: prev.phone.slice(0, max),
              }));
              setErrors((prev) => ({ ...prev, phoneCode: "", phone: "" }));
            }}
          >
            {countryCodes.map((c) => (
              <option key={c.value} value={c.label}>
                {flagOf(c.description)} {c.label}
              </option>
            ))}
          </select>
        </label>
        {withError(
          "phone",
          <Input
            type="tel"
            label={t("groupbooking.v2.form.label.mobile")}
            id="gb-phone"
            showErrorMessage={!!errors.phone}
            required
            value={one.phone}
            error={errors.phone}
            // ⚠️  `maxLength` ON THE ELEMENT, NOT JUST A SLICE IN THE HANDLER.
            //
            // Slicing here alone did not hold. `Input` keeps its own `inputVal` state and
            // re-syncs it only in an effect keyed on the `value` PROP — so once the cap is
            // reached the sliced value equals the previous one, the prop never changes, the
            // effect never fires, and the element keeps every extra digit typed. It showed
            // 19 digits in a field capped at 10.
            //
            // `Input` forwards `maxLength` straight to the <input>, so the browser refuses
            // the keystroke and the two states cannot drift. The slice stays as a guard for
            // paste paths.
            maxLength={phoneMaxDigits}
            onChange={(v: string) =>
              setField(setOne, "phone")(v.replace(/\D/g, "").slice(0, phoneMaxDigits))
            }
            containerClassName={styles.grow}
          />,
        )}
      </div>

      {/* The API refuses a lead with no location — see the note at the top. */}
      <div className={styles.region}>
        <RegionDropdown
          error={errors.region}
          setErrors={setErrors}
          selectedRegion={region.name}
          setSelectedRegion={(chosen: any) => {
            setRegion(chosen);
            setErrors((prev) => ({ ...prev, region: "" }));
          }}
        />
        {errors.region ? (
          <span className={styles.error} role="alert">
            {errors.region}
          </span>
        ) : null}
      </div>

      {withError(
        "students",
        <Input
          type="number"
          label={t("groupbooking.v2.form.label.students")}
          id="gb-students"
          showErrorMessage={!!errors.students}
          required
          value={one.students}
          error={errors.students}
          onChange={setField(setOne, "students")}
        />,
      )}

      <button type="submit" className={styles.submit} disabled={busy}>
        {busy ? t("groupbooking.v2.form.button.wait") : t("groupbooking.v2.form.button.next")}
      </button>
    </form>
  );
};

export default wrapperHOC(GroupBookingForm, {
  componentName: "GroupBookingForm-GroupBookingV2",
  showForChina: true,
});
